// LocationSelector - Shows venue selection for restaurant groups
// Displays list of locations with "Find Closest" option using geolocation

import React, { useState, useMemo } from 'react';

export function LocationSelector({ group, venues, onSelectVenue, onFindClosest }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [findingLocation, setFindingLocation] = useState(false);

  // Filter venues by search query
  const filteredVenues = useMemo(() => {
    if (!searchQuery.trim()) return venues;
    
    const query = searchQuery.toLowerCase();
    return venues.filter(venue => 
      venue.venuename?.toLowerCase().includes(query) ||
      (venue.address || venue.address_line1 || venue.addr1)?.toLowerCase().includes(query) ||
      venue.city?.toLowerCase().includes(query) ||
      venue.state?.toLowerCase().includes(query) ||
      (venue.zipcode || venue.zip)?.includes(query)
    );
  }, [venues, searchQuery]);

  const handleFindClosest = async () => {
    setFindingLocation(true);
    try {
      await onFindClosest();
    } finally {
      setFindingLocation(false);
    }
  };

  // Handle venue selection - navigate to venue's slug URL
  const handleVenueClick = (venue) => {
    // Navigate to venue slug URL for proper routing
    if (venue.slug) {
      window.location.href = `/${venue.slug}`;
    } else {
      // Fallback to callback if no slug
      onSelectVenue(venue);
    }
  };

  // Format address. The online group/resolve endpoints return the street line
  // as `address` (sourced from onlineSettings.headerLine2 || venue_data.addr1),
  // so check that first; keep the other aliases as fallbacks.
  const formatAddress = (venue) => {
    const parts = [];
    const street = venue.address || venue.address_line1 || venue.addr1 || venue.headerLine2;
    if (street) parts.push(street);
    if (venue.city) parts.push(venue.city);
    if (venue.state) parts.push(venue.state);
    if (venue.zipcode || venue.zip) parts.push(venue.zipcode || venue.zip);
    return parts.join(', ');
  };

  // Format hours for display
  const formatHours = (venue) => {
    // This is simplified - in real app would parse availableDays_json
    return venue.hours_display || 'Hours vary by location';
  };

  return (
    <div className="location-selector">
      <div className="location-selector__header">
        {(group?.logo || group?.styleSettings?.urlLogo) && (
          <img 
            src={group.logo || group.styleSettings.urlLogo} 
            alt={group.name || 'Restaurant Group'} 
            className="location-selector__logo"
          />
        )}
        <h1 className="location-selector__title">
          {group?.name || 'Select a Location'}
        </h1>
        {group?.description && (
          <p className="location-selector__description">{group.description}</p>
        )}
      </div>

      <div className="location-selector__actions">
        <button 
          className="btn btn--primary btn--find-closest"
          onClick={handleFindClosest}
          disabled={findingLocation}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="10" r="3"/>
            <path d="M12 2a8 8 0 0 0-8 8c0 5.4 7 12 8 12s8-6.6 8-12a8 8 0 0 0-8-8z"/>
          </svg>
          {findingLocation ? 'Finding...' : 'Find Closest Location'}
        </button>
        
        <div className="location-selector__divider">
          <span>or select a location</span>
        </div>

        <div className="location-selector__search">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8"/>
            <path d="m21 21-4.35-4.35"/>
          </svg>
          <input
            type="text"
            placeholder="Search by name, address, or zip code..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </div>

      <div className="location-selector__list">
        {filteredVenues.length === 0 ? (
          <div className="location-selector__empty">
            <p>No locations found matching "{searchQuery}"</p>
            <button 
              className="btn btn--link"
              onClick={() => setSearchQuery('')}
            >
              Clear search
            </button>
          </div>
        ) : (
          filteredVenues.map(venue => (
            <div 
              key={venue.venueid}
              className="location-card"
              onClick={() => handleVenueClick(venue)}
            >
              <div className="location-card__content">
                <h3 className="location-card__name">{venue.venuename}</h3>
                <p className="location-card__address">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 2a8 8 0 0 0-8 8c0 5.4 7 12 8 12s8-6.6 8-12a8 8 0 0 0-8-8z"/>
                    <circle cx="12" cy="10" r="3"/>
                  </svg>
                  {formatAddress(venue)}
                </p>
                {venue.phone && (
                  <p className="location-card__phone">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
                    </svg>
                    {venue.phone}
                  </p>
                )}
                {venue.distance && (
                  <p className="location-card__distance">
                    {venue.distance.toFixed(1)} miles away
                  </p>
                )}
              </div>
              <div className="location-card__arrow">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="9 18 15 12 9 6"/>
                </svg>
              </div>
            </div>
          ))
        )}
      </div>

      <style>{`
        .location-selector {
          min-height: 100vh;
          background: var(--bg, #f8f9fa);
          padding: 2rem 1rem;
        }

        .location-selector__header {
          text-align: center;
          max-width: 600px;
          margin: 0 auto 2rem;
        }

        .location-selector__logo {
          max-width: 200px;
          max-height: 80px;
          object-fit: contain;
          margin-bottom: 1rem;
        }

        .location-selector__title {
          font-size: 1.75rem;
          font-weight: 700;
          color: var(--text, #1a1a1a);
          margin: 0 0 0.5rem;
        }

        .location-selector__description {
          color: var(--text-muted, #6b7280);
          font-size: 1rem;
          margin: 0;
        }

        .location-selector__actions {
          max-width: 600px;
          margin: 0 auto 2rem;
        }

        .btn--find-closest {
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.75rem;
          padding: 1rem 1.5rem;
          font-size: 1.1rem;
          font-weight: 600;
          background: var(--primary, #2563eb);
          color: white;
          border: none;
          border-radius: var(--border-radius, 12px);
          cursor: pointer;
          transition: all 0.2s;
        }

        .btn--find-closest:hover:not(:disabled) {
          background: var(--primary-hover, #1d4ed8);
          transform: translateY(-1px);
        }

        .btn--find-closest:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }

        .btn--find-closest svg {
          width: 20px;
          height: 20px;
        }

        .location-selector__divider {
          display: flex;
          align-items: center;
          margin: 1.5rem 0;
        }

        .location-selector__divider::before,
        .location-selector__divider::after {
          content: '';
          flex: 1;
          height: 1px;
          background: var(--border, #e5e7eb);
        }

        .location-selector__divider span {
          padding: 0 1rem;
          color: var(--text-muted, #6b7280);
          font-size: 0.875rem;
        }

        .location-selector__search {
          position: relative;
        }

        .location-selector__search svg {
          position: absolute;
          left: 1rem;
          top: 50%;
          transform: translateY(-50%);
          width: 20px;
          height: 20px;
          color: var(--text-muted, #6b7280);
        }

        .location-selector__search input {
          width: 100%;
          padding: 1rem 1rem 1rem 3rem;
          font-size: 1rem;
          border: 1px solid var(--border, #e5e7eb);
          border-radius: var(--border-radius, 12px);
          background: var(--bg-secondary, white);
          color: var(--text, #1a1a1a);
          transition: all 0.2s;
        }

        .location-selector__search input:focus {
          outline: none;
          border-color: var(--primary, #2563eb);
          box-shadow: 0 0 0 3px var(--primary-light, rgba(37, 99, 235, 0.1));
        }

        .location-selector__list {
          max-width: 600px;
          margin: 0 auto;
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .location-selector__empty {
          text-align: center;
          padding: 3rem 1rem;
          color: var(--text-muted, #6b7280);
        }

        .btn--link {
          background: none;
          border: none;
          color: var(--primary, #2563eb);
          cursor: pointer;
          font-size: 1rem;
          text-decoration: underline;
          margin-top: 0.5rem;
        }

        .location-card {
          display: flex;
          align-items: center;
          padding: 1.25rem;
          background: var(--bg-secondary, white);
          border: 1px solid var(--border, #e5e7eb);
          border-radius: var(--border-radius, 12px);
          cursor: pointer;
          transition: all 0.2s;
        }

        .location-card:hover {
          border-color: var(--primary, #2563eb);
          box-shadow: var(--card-shadow-hover, 0 4px 12px rgba(0,0,0,0.1));
          transform: translateY(-2px);
        }

        .location-card__content {
          flex: 1;
          min-width: 0;
        }

        .location-card__name {
          font-size: 1.125rem;
          font-weight: 600;
          color: var(--text, #1a1a1a);
          margin: 0 0 0.5rem;
        }

        .location-card__address,
        .location-card__phone,
        .location-card__distance {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.875rem;
          color: var(--text-muted, #6b7280);
          margin: 0.25rem 0;
        }

        .location-card__address svg,
        .location-card__phone svg {
          width: 16px;
          height: 16px;
          flex-shrink: 0;
        }

        .location-card__distance {
          color: var(--primary, #2563eb);
          font-weight: 500;
        }

        .location-card__arrow {
          margin-left: 1rem;
          color: var(--text-muted, #6b7280);
        }

        .location-card__arrow svg {
          width: 24px;
          height: 24px;
        }

        @media (max-width: 640px) {
          .location-selector {
            padding: 1rem;
          }

          .location-selector__title {
            font-size: 1.5rem;
          }
        }
      `}</style>
    </div>
  );
}

export default LocationSelector;
